<template>
  <div>
    <PhotoStatsHeader
      :sdate="sdate"
      :edate="edate"
      :bwphotos="bwphotos"
      :colorphotos="colorphotos"
      :up="up"
      :tp="tp"
      :devices="devices"
      @update="handleUpdate"
      @export="handleExport"
    />

    <div class="bg-white rounded shadow-sm">
      <div
        v-if="isLoading"
        class="photo-stats-skeleton-wrap"
      >
        <div class="photo-stats-skeleton-title"></div>
        <div class="photo-stats-skeleton-chart">
          <div class="photo-stats-skeleton-bar" v-for="index in 7" :key="`photo-skeleton-bar-${index}`"></div>
        </div>
        <div class="photo-stats-skeleton-line"></div>
        <div class="photo-stats-skeleton-line short"></div>
      </div>

      <div
        v-else-if="!chartData.length"
        class="d-flex justify-content-center align-items-center"
        style="height: 40vh"
      >
        <div class="text-muted">No data available.</div>
      </div>

      <PhotoStatsChart v-else :data="chartData" ref="photoChart" />
    </div>

    <div class="bg-white rounded shadow-sm p-3 mt-3 mb-3">
      <div class="row">
        <div class="col-12 col-md-6 mb-3 mb-md-0">
          <div class="mb-3 mt-2">Total photos: {{ totalPhotos ?? 0 }}</div>
          <div class="mt-2">Total money: {{ totalMoney ?? "0,00" }} ₾</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/reportService";
import PhotoStatsHeader from "@/components/PhotoStats/PhotoStatsHeader.vue";
import PhotoStatsChart from "@/components/PhotoStats/PhotoStatsChart.vue";

export default {
  name: "PhotoStats",
  components: {
    PhotoStatsHeader,
    PhotoStatsChart,
  },
  data() {
    const now = new Date();
    return {
      sdate: new Date(now.setDate(now.getDate() - 7))
        .toISOString()
        .substring(0, 10),
      edate: new Date().toISOString().substring(0, 10),
      bwphotos: true,
      colorphotos: true,
      up: 1,
      tp: 0,
      device: 0,
      chartData: [],
      totalPhotos: null,
      totalMoney: null,
      isLoading: false,
      devices: {},
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const payload = {
        sdate: this.sdate,
        edate: this.edate,
        up: this.up,
        tp: this.tp,
        clientSel: 0,
        device: this.device,
        colorphotos: this.colorphotos ? 1 : 0,
        bwphotos: this.bwphotos ? 1 : 0,
      };

      this.isLoading = true;

      try {
        const res = await reportService.getPhotoStats(payload);
        this.chartData = res.data;
        this.devices = res.devices;
        this.totalPhotos = res.total_photos;
        this.totalMoney = res.total_money;
      } catch (e) {
        console.error("Error loading photo stats:", e);
      } finally {
        this.isLoading = false;
      }
    },
    async handleExport(filters) {
      const payload = {
        sdate: filters.sdate,
        edate: filters.edate,
        colorphotos: filters.colorphotos ? 1 : 0,
        bwphotos: filters.bwphotos ? 1 : 0,
        clientSel: 0,
        device: filters.device,
        lang: filters.lang || "ge",
        up: filters.up,
        tp: filters.tp,
      };

      if (filters.format === "xlsx") {
        try {
          const response = await reportService.getPhotoStatsXlsx(payload);
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `photo-stats-${payload.sdate}_to_${payload.edate}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.error("Export failed:", e);
        }
      } else if (filters.format === "doc") {
        const queryParams = new URLSearchParams(payload).toString();
        const downloadUrl =
          process.env.VUE_APP_API_BASE_URL +
          `/report/photo-stats/export-doc?${queryParams}`;

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `photo-stats-${payload.sdate}_to_${payload.edate}.docx`
        );
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("Unsupported export format:", filters.format);
      }
    },
    handleUpdate({ sdate, edate, bwphotos, colorphotos, up, tp, device }) {
      this.sdate = sdate;
      this.edate = edate;
      this.bwphotos = bwphotos;
      this.colorphotos = colorphotos;
      this.up = up;
      this.tp = tp;
      this.device = device;
      this.fetchData();
    },
  },
};
</script>

<style scoped>
.photo-stats-skeleton-wrap {
  min-height: 40vh;
  display: grid;
  align-content: center;
  gap: 0.85rem;
  padding: 1rem;
}

.photo-stats-skeleton-title,
.photo-stats-skeleton-line,
.photo-stats-skeleton-bar {
  background: linear-gradient(90deg, #e9ecef 0%, #f8f9fa 50%, #e9ecef 100%);
  background-size: 220% 100%;
  animation: photo-stats-skeleton-shimmer 1.2s linear infinite;
  border-radius: 0.35rem;
}

.photo-stats-skeleton-title {
  height: 0.95rem;
  width: 34%;
}

.photo-stats-skeleton-line {
  height: 0.82rem;
  width: 92%;
}

.photo-stats-skeleton-line.short {
  width: 70%;
}

.photo-stats-skeleton-chart {
  height: 210px;
  border: 1px solid #edf0f2;
  border-radius: 0.5rem;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  align-items: end;
  gap: 0.6rem;
  padding: 0.75rem;
  background: #fbfcfd;
}

.photo-stats-skeleton-bar {
  height: 35%;
}

.photo-stats-skeleton-bar:nth-child(2) {
  height: 52%;
}

.photo-stats-skeleton-bar:nth-child(3) {
  height: 68%;
}

.photo-stats-skeleton-bar:nth-child(4) {
  height: 45%;
}

.photo-stats-skeleton-bar:nth-child(5) {
  height: 74%;
}

.photo-stats-skeleton-bar:nth-child(6) {
  height: 58%;
}

.photo-stats-skeleton-bar:nth-child(7) {
  height: 64%;
}

@keyframes photo-stats-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
